/**
 * Componente que muestra la información detallada de una carrera de Fórmula 1
 * Incluye podio, tiempos, puntos y datos de la carrera (vueltas/kilómetros)
 * Se adapta dinámicamente según la disponibilidad de datos
 * 
 * @param {Object} props - Propiedades del componente
 * @param {boolean} props.showResults - Indica si debe mostrar los resultados del podio
 * @param {Object} props.item - Datos completos del evento de F1
 * @param {Object} props.item.race_results - Resultados específicos de la carrera
 * @param {Array} props.item.race_results.podium - Array con los pilotos del podio
 * @param {number} props.item.race_results.laps - Número de vueltas de la carrera
 * @param {string} props.item.race_results.raceLength - Distancia de la carrera en kilómetros
 * 
 * @returns {JSX.Element} Información completa de la carrera de F1
 */
export default function InformationRaceComponent({ showResults, item }) {

  /**
   * Extrae el podio de los resultados de la carrera
   * Fallback a array vacío si no hay datos
   */
  const podium = item?.race_results?.podium || [];

  /**
   * Determina si todos los puntos están vacíos (son "0.000")
   * Se usa para ocultar la columna de puntos cuando no hay datos válidos
   */
  const pointsEmpty = podium.length > 0 && !podium.some(p => String(p.points) !== "0.000");

  /**
   * Determina si todos los tiempos están vacíos
   * Se usa para ocultar la columna de tiempos cuando no hay datos válidos
   */
  const timeEmpty = podium.length > 0 && !podium.some(p => String(p.timeString || "").trim().length !== 0);

  /**
   * Renderiza la información de distancia de la carrera
   * Maneja diferentes combinaciones de vueltas y kilómetros
   */
  const renderRaceDistance = () => {
    const raceResults = item?.race_results;
    if (!raceResults) return null;

    const hasLaps = raceResults.laps !== 0;
    const hasLength = String(raceResults.raceLength || "").trim().length > 0;

    if (hasLaps && hasLength) {
      return (
        <p className="total-time-copy">
          {raceResults.laps} vueltas | {raceResults.raceLength} kilómetros
        </p>
      );
    } else if (hasLaps && !hasLength) {
      return <p className="total-time-copy">{raceResults.laps} vueltas</p>;
    } else if (!hasLaps && hasLength) {
      return <p className="total-time-copy">{raceResults.raceLength} kilómetros</p>;
    }

    return null;
  };

  return (
    <>
      {/* Sección de resultados del podio */}
      {showResults && (
        <>
          {/* Encabezado de columnas */}
          <div className="container-title-time">
            <p className="title-f1-pos">POS</p>
            <p className="title-f1-piloto">PILOTO</p>
            <p className="title-f1-time">{timeEmpty ? "" : "TIEMPO"}</p>
            <p className="title-f1-ptos">{pointsEmpty ? "" : "PTOS"}</p>
          </div>

          {/* Filas del podio */}
          <div className="container-results-f1">
            {podium.map((position, idx) => (
              <div key={idx} className="results-f1-info">

                {/* Posición en el podio */}
                <div className="cardf1-podium-pos"> {idx + 1} </div>

                {/* Información del piloto */}
                <div className="cardf1-podium-piloto">
                  <div className="card-img-name">
                    <div
                      className={`bandera-f1 bandera-${position.nationalityCode}`}
                      role="img"
                      aria-label={`Bandera de ${position.nationalityCode}`}
                      title={`País: ${position.nationalityCode}`}
                    />
                    <p className="text-break">{String(position.firstName).toUpperCase()} {String(position.lastName).toUpperCase()}</p>
                  </div>
                  <p className="subtitle-podium text-break">
                    {String(position.team).toUpperCase()} · #{position.carNumber}
                  </p>
                </div>

                {/* Tiempo de la carrera */}
                <div className="cardf1-podium-time">
                  {!timeEmpty && (idx === 0 ? position.timeString : position.timeDiference)}
                </div>

                {/* Puntos obtenidos */}
                <div className="cardf1-podium-pts">
                  {!pointsEmpty ? String(position.points).split(".")[0] : ""}
                </div>
              </div>
            ))}
          </div>
        </>
      )}

      {/* Información de distancia de la carrera */}
      <div className="container-total-time">
        {renderRaceDistance()}
      </div>
    </>
  );
}